import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import { ProfileSettingsModal, FieldTextInput, FieldSelectModern, Form } from '../../components';
import { generateYearsList } from '../../util/dates';
import { SlOptionsVertical } from 'react-icons/sl';
import { AiOutlineClose, AiOutlineDelete } from 'react-icons/ai';

import css from './ProfileSettingsEducationForm.css';

const REQUIRED_FIELDS = ['where', 'degree', 'industry', 'when'];

const validateForm = (values, intl) => {
  const errors = {};
  REQUIRED_FIELDS.forEach(field => {
    if (!values[field]) {
      errors[field] = intl.formatMessage({ id: 'ProfileSettingsEducationForm.required' });
    }
  });
  return errors;
};

const buildInitialValues = editingItem => {
  if (!editingItem) {
    return {
      where: '',
      degree: '',
      industry: '',
      when: null,
    };
  }

  const where = editingItem.where || editingItem.school || '';
  const degree = editingItem.degree || '';
  const industry = editingItem.industry || editingItem.education || '';
  const when = editingItem.when || editingItem.graduationYear || null;

  return {
    where,
    degree,
    industry,
    when: when ? { key: when, label: when, value: when } : null,
  };
};

const ProfileSettingsEducationForm = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    editingItem,
    onSubmit,
    onDelete,
    updateInProgress,
    intl,
  } = props;

  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteConfirmPending, setDeleteConfirmPending] = useState(false);
  const deleteTimeoutRef = useRef(null);

  const yearsList = useMemo(() => generateYearsList(1900, 5), []);
  const initialValues = useMemo(() => buildInitialValues(editingItem), [editingItem]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setMenuOpen(false);
      setDeleteConfirmPending(false);
    }
    return () => {
      if (deleteTimeoutRef.current) clearTimeout(deleteTimeoutRef.current);
    };
  }, [isOpen]);

  const isNew = !editingItem;

  const handleDeleteClick = useCallback(() => {
    if (deleteConfirmPending) {
      if (deleteTimeoutRef.current) clearTimeout(deleteTimeoutRef.current);
      onDelete(editingItem.id);
      onClose();
    } else {
      setDeleteConfirmPending(true);
      deleteTimeoutRef.current = setTimeout(() => {
        setDeleteConfirmPending(false);
      }, 3000);
    }
  }, [deleteConfirmPending, editingItem, onDelete, onClose]);

  // Labels
  const labels = {
    title: intl.formatMessage({ id: 'ProfileSettingsEducationForm.title' }),
    subtitle: intl.formatMessage({ id: 'ProfileSettingsEducationForm.subtitle' }),
    save: intl.formatMessage({ id: 'ProfileSettingsPage.saveButton' }),
    school: intl.formatMessage({ id: 'ProfileSettingsEducationForm.schoolLabel' }),
    schoolPlaceholder: intl.formatMessage({ id: 'ProfileSettingsEducationForm.schoolPlaceholder' }),
    degree: intl.formatMessage({ id: 'ProfileSettingsEducationForm.degreeLabel' }),
    degreePlaceholder: intl.formatMessage({ id: 'ProfileSettingsEducationForm.degreePlaceholder' }),
    education: intl.formatMessage({ id: 'ProfileSettingsEducationForm.educationLabel' }),
    educationPlaceholder: intl.formatMessage({
      id: 'ProfileSettingsEducationForm.educationPlaceholder',
    }),
    graduationYear: intl.formatMessage({ id: 'ProfileSettingsEducationForm.graduationYearLabel' }),
    graduationYearPlaceholder: intl.formatMessage({
      id: 'ProfileSettingsEducationForm.graduationYearPlaceholder',
    }),
    cancel: intl.formatMessage({ id: 'ProfileSettingsEducationForm.cancel' }),
    close: intl.formatMessage({ id: 'ProfileSettingsEducationForm.close' }),
    discardChanges: intl.formatMessage({ id: 'ProfileSettingsEducationForm.discardChanges' }),
    deleteLabel: intl.formatMessage({ id: 'ProfileSettingsEducationForm.delete' }),
    confirmDeletion: intl.formatMessage({ id: 'ProfileSettingsEducationForm.confirmDeletion' }),
  };

  return (
    <FinalForm
      key={isOpen ? 'open' : 'closed'}
      initialValues={initialValues}
      validate={values => validateForm(values, intl)}
      onSubmit={values => {
        onSubmit({ item: values });
        onClose();
      }}
      render={({ handleSubmit, dirty, invalid, form }) => {
        const isFormDirty = dirty;
        const submitDisabled = !isFormDirty || invalid;

        // Build menu items
        const buildMenuItems = () => {
          const items = [];

          // 1. Cancel / Close / Discard changes
          if (isNew) {
            items.push({
              key: 'cancel',
              label: labels.close,
              icon: <AiOutlineClose className={css.menuItemIcon} />,
              onClick: () => {
                setMenuOpen(false);
                onClose();
              },
              variant: 'default',
            });
          } else if (isFormDirty) {
            items.push({
              key: 'discard',
              label: labels.discardChanges,
              icon: <AiOutlineClose className={css.menuItemIcon} />,
              onClick: () => {
                form.reset();
                setMenuOpen(false);
              },
              variant: 'default',
            });
          } else {
            items.push({
              key: 'close',
              label: labels.close,
              icon: <AiOutlineClose className={css.menuItemIcon} />,
              onClick: () => {
                setMenuOpen(false);
                onClose();
              },
              variant: 'default',
            });
          }

          // 2. Delete (only existing items)
          if (!isNew) {
            items.push({
              key: 'delete',
              label: deleteConfirmPending ? labels.confirmDeletion : labels.deleteLabel,
              icon: <AiOutlineDelete className={css.menuItemIcon} />,
              onClick: handleDeleteClick,
              variant: 'danger',
              confirm: deleteConfirmPending,
            });
          }

          return items;
        };

        const menuItems = buildMenuItems();

        const kebabMenu = (
          <div className={css.kebabWrapper}>
            <button
              type="button"
              className={css.kebabTrigger}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <SlOptionsVertical className={css.kebabIcon} />
            </button>
            {menuOpen ? (
              <>
                <div
                  className={css.menuBackdrop}
                  onClick={() => {
                    setMenuOpen(false);
                    setDeleteConfirmPending(false);
                  }}
                />
                <div className={css.kebabDropdown}>
                  {menuItems.map(item => {
                    const className = item.disabled
                      ? css.menuItemDisabled
                      : item.confirm
                      ? css.menuItemConfirm
                      : item.variant === 'danger'
                      ? css.menuItemDanger
                      : css.menuItem;

                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={className}
                        onClick={item.disabled ? undefined : item.onClick}
                        disabled={item.disabled}
                      >
                        {item.icon}
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : null}
          </div>
        );

        return (
          <ProfileSettingsModal
            id="education"
            isOpen={isOpen}
            onClose={onClose}
            onManageDisableScrolling={onManageDisableScrolling}
            title={labels.title}
            subtitle={labels.subtitle}
            headerAction={kebabMenu}
            onSubmit={handleSubmit}
            submitDisabled={submitDisabled}
            submitInProgress={updateInProgress}
            submitLabel={labels.save}
          >
            <Form className={css.formContent} onSubmit={() => {}}>
              {/* School */}
              <FieldTextInput
                id="where"
                name="where"
                type="text"
                label={labels.school}
                placeholder={labels.schoolPlaceholder}
              />

              {/* Degree */}
              <FieldTextInput
                id="degree"
                name="degree"
                type="text"
                label={labels.degree}
                placeholder={labels.degreePlaceholder}
              />

              {/* Education */}
              <FieldTextInput
                id="industry"
                name="industry"
                type="text"
                label={labels.education}
                placeholder={labels.educationPlaceholder}
              />

              {/* Graduation year */}
              <FieldSelectModern
                id="when"
                name="when"
                options={yearsList}
                placeholder={labels.graduationYearPlaceholder}
                label={labels.graduationYear}
              />
            </Form>
          </ProfileSettingsModal>
        );
      }}
    />
  );
};

ProfileSettingsEducationForm.defaultProps = {
  editingItem: null,
  updateInProgress: false,
};

ProfileSettingsEducationForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  editingItem: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  updateInProgress: PropTypes.bool,
  intl: intlShape.isRequired,
};

export default injectIntl(ProfileSettingsEducationForm);
